<script module lang="ts">
  export type CardProfile = Pick<
    Profile,
    'id' | 'username' | 'emoji_character' | 'color'
  > & {
    streak_days?: number
    streak_requires_session_today?: boolean
    points?: number
    multiplier?: number
    plan?: Profile['plan']
  }
  export const PROFILE_CARD_SIZES = [
    'small',
    'default',
    'large',
    'huge',
  ] as const
  export type ProfileCardSize = (typeof PROFILE_CARD_SIZES)[number]
</script>

<script lang="ts">
  import { resolve } from '$app/paths'
  import { page } from '$app/state'
  import type { Profile } from '$lib/types'
  import { profileColors } from '$lib/utils/colors'
  import Emoji from './Emoji.svelte'
  import ProfileLevel from './profile/ProfileLevel.svelte'
  import Streak from './Streak.svelte'

  interface Props {
    profile: CardProfile
    showStreak?: boolean
    showIdIfNoUsername?: boolean
    size?: ProfileCardSize
    clickable?: boolean
    /** Whether this profile card is displayed in the participants list. */
    insideParticipantsList?: boolean
    children?: import('svelte').Snippet
  }

  let {
    profile,
    showStreak = true,
    showIdIfNoUsername = true,
    size = 'default',
    clickable = true,
    insideParticipantsList = false,
    children,
  }: Props = $props()

  let hasLevel = $derived(profile.points !== undefined && profile.multiplier)

  let session = $derived(page.data.session)
</script>

<div
  class="profile-card size-{size} plan-{profile.plan}"
  class:inside-participants-list={insideParticipantsList}
  style:--_color={profileColors[profile.color]}
  class:with-level={hasLevel}
  class:with-streak={showStreak && profile.streak_days}
>
  {#if hasLevel}
    <div class="level-wrapper">
      <ProfileLevel points={profile.points!} multiplier={profile.multiplier!} />
    </div>
  {/if}

  {#if showStreak && profile.streak_days}
    <div class="streak-wrapper">
      <Streak
        streakDays={profile.streak_days}
        requiresSessionToday={session?.user.id === profile.id &&
          profile.streak_requires_session_today}
        format="long"
        infoTooltip
      />
    </div>
  {/if}

  {#if profile.username || showIdIfNoUsername}
    {@const displayName = profile.username || profile.id}
    {#if clickable}
      <a
        href={resolve(`/(app)/+[usernameOrId=username_or_id]`, {
          usernameOrId: profile.username || profile.id,
        })}
        class="name"
      >
        {displayName}
      </a>
    {:else}
      <span class="name">
        {displayName}
      </span>
    {/if}
  {/if}
  <div class="emoji">
    <Emoji character={profile.emoji_character} />
  </div>
  {#if children}
    <div class="postfix">
      {@render children?.()}
    </div>
  {/if}
</div>

<style lang="postcss">
  .profile-card {
    --_card-height: 2.25em;
    --_padding: 0.25em;
    --_border-size: 1px;
    --_card-content-height: calc(
      var(--_card-height) - var(--_padding) * 2 - var(--_border-size) * 2
    );
    --_emoji-size: calc(var(--_card-height) * 0.65);
    display: inline-grid;
    grid-template-columns:
      [level-start] auto [level-end
      streak-start] auto [streak-end
      name-start] minmax(0, 1fr) [name-end
      emoji-start] auto [emoji-end
      postfix-start] auto [postfix-end];
    flex-shrink: 0;
    align-items: center;
    border-radius: var(--radius-full);
    background-color: var(--_color);
    padding: var(--_padding);
    max-width: 100%;
    height: var(--_card-height);
    line-height: 1.6em;
    &.size-small {
      /* --_padding: 0.4em; */
      font-size: var(--scale-00);
    }
    &.size-default {
      font-size: var(--scale-0);
    }
    &.size-large {
      margin-inline: -0.25em;
      font-size: var(--scale-2);
    }
    &.size-huge {
      margin-inline: -0.25em;
      font-size: var(--scale-3);
    }

    & .name {
      grid-column: name;
      width: max-content;
      max-width: 100%;
      color: var(--color-text);
      font-family: var(--font-code);
      white-space: nowrap;
      &:hover {
        text-decoration: underline;
        text-decoration-thickness: 2px;
      }
    }
    &:not(.with-level)&:not(.with-streak) .name {
      margin-left: 0.5em;
    }
    &:not(.inside-participants-list) .name {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &.inside-participants-list .name {
      @media (min-width: 700px) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
  .level-wrapper {
    grid-column: level;
    margin-right: 0.5em;
    width: var(--_card-content-height);
    height: var(--_card-content-height);
  }
  .streak-wrapper {
    display: flex;
    grid-column: streak;
    margin-right: 0.25em;
    margin-left: -0.25em;
  }

  .emoji {
    grid-column: emoji;
    flex-shrink: 0;
    margin-right: 0.125em;
    margin-left: 0.5em;
    width: var(--_emoji-size);
    max-width: none;
    height: var(--_emoji-size);
  }
  .postfix {
    grid-column: postfix;
    flex-shrink: 0;
    margin-left: 0.25em;
  }
</style>
